import React from 'react';
import { WrenchIcon } from '@heroicons/react/24/outline';
import { VIEW_SERVICE_REQUEST_LABELS as L } from '../../constants/labels';
import { getClientInitials } from '@/epics/clientManagement/hooks/useViewClient';
import type { ViewServiceRequestDetail } from '../../types/viewServiceRequestType';
import {
  formatRole,
  formatSubmittedOn,
  orDash,
} from './viewServiceRequestHelpers';
import { InfoField, SectionCard } from './viewServiceRequestUI';

interface SRSummaryTabProps {
  detail: ViewServiceRequestDetail | undefined;
}

const SRSummaryTab: React.FC<SRSummaryTabProps> = ({ detail }) => {
  return (
    <div className="flex flex-col gap-6">
      <SectionCard
        title={L.SECTION_USER_DATA}
        testId="view-sr-section-user-data"
      >
        <div className="flex min-w-0 flex-col gap-2">
          <p className="text-bodySmall font-normal text-on-surface-variant">
            {L.FIELD_PROFILE_PICTURE}
          </p>
          <div
            className="flex items-center justify-center shrink-0 rounded-full bg-[#E8EEF0] text-[#223746] font-medium overflow-hidden"
            style={{
              width: '64px',
              height: '64px',
              fontSize: '22px',
              lineHeight: '32px',
            }}
            data-testid="view-sr-field-profile-picture"
          >
            {detail?.client_profile?.url?.trim() ? (
              <img
                src={detail.client_profile.url}
                alt=""
                className="w-full h-full object-cover"
              />
            ) : (
              getClientInitials(detail?.client_name)
            )}
          </div>
        </div>
        <div
          className="grid gap-x-8 gap-y-6"
          style={{ gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)' }}
        >
          <InfoField
            label={L.FIELD_CLIENT_NAME}
            value={orDash(detail?.client_name)}
            testId="view-sr-field-client-name"
          />
          <InfoField
            label={L.FIELD_CLIENT_ID}
            value={orDash(detail?.client_id)}
            testId="view-sr-field-client-id"
          />
          <InfoField
            label={L.FIELD_ROLE}
            value={formatRole(detail?.role)}
            testId="view-sr-field-role"
          />
          <InfoField
            label={L.FIELD_SUBMITTED_ON}
            value={formatSubmittedOn(detail?.submitted_on)}
            testId="view-sr-field-submitted-on"
          />
        </div>
      </SectionCard>

      <SectionCard
        title={L.SECTION_SELECTED_SERVICE}
        testId="view-sr-section-service"
      >
        <div className="flex items-start gap-3">
          <span className="flex items-center justify-center w-10 h-10 rounded-full bg-[#F3F4F6] shrink-0 mt-0.5">
            <WrenchIcon className="w-5 h-5 text-[#474A4C]" />
          </span>
          <div className="flex-1 min-w-0">
            <p
              className="font-normal text-[#181A1B] text-sm break-words"
              data-testid="view-sr-service-type-name"
            >
              {orDash(detail?.service_type_name)}
            </p>
            <p
              className="text-sm text-[#474A4C]"
              data-testid="view-sr-service-type-price"
            >
              {orDash(detail?.service_type_price)}
            </p>
          </div>
        </div>
        {detail?.service_type_description?.trim() && (
          <p
            className="text-body-large text-[#474A4C] whitespace-pre-wrap break-words"
            data-testid="view-sr-service-type-description"
          >
            {detail.service_type_description}
          </p>
        )}
      </SectionCard>
    </div>
  );
};

export default SRSummaryTab;
